import clsx from "clsx"
import NextImage, { type ImageProps as NextImageProps } from "next/image"
import { type HTMLAttributes, type CSSProperties } from "react"
import { twMerge } from "tailwind-merge"

type Props = Omit<
  NextImageProps,
  "className" | "objectFit" | "objectPosition"
> &
  HTMLAttributes<HTMLDivElement> & {
    isRounded?: boolean
    // These are deprecated in NextImage and meant to be passed as style. Our custom wrapper handles this
    objectFit?: CSSProperties["objectFit"]
    objectPosition?: CSSProperties["objectPosition"]
    imageClassName?: string
  }

/** @deprecated */
export type { Props as ImageProps }

/*
 * For small images, default quality to a bit higher than normally. NextImage by default uses 75, but
 * lets try defaulting to 80 for a bit better quality. Images are hopefully already optimized anyway.
 */
const SMALL_IMAGE_CONSTRAIN = 120
const calcQuality = ({
  width,
  height,
}: Pick<NextImageProps, "width" | "height">) =>
  (width ? Number(width) : SMALL_IMAGE_CONSTRAIN + 1) <=
    SMALL_IMAGE_CONSTRAIN &&
  (height ? Number(height) : SMALL_IMAGE_CONSTRAIN + 1) <= SMALL_IMAGE_CONSTRAIN
    ? 90
    : 80

/** @deprecated */
export const Image = ({
  isRounded,
  className,
  objectFit = "cover",
  objectPosition = "center",
  style,
  children,
  quality,
  imageClassName,
  ...props
}: Props) => {
  const styleProps = style ?? {}

  if (isRounded) {
    // Safari cant use border-radius+overflow without isolating the layer
    Object.assign(styleProps, { isolation: "isolate" })
  }

  return (
    <div
      className={twMerge(
        "flex",
        isRounded && "overflow-hidden rounded-full",
        className
      )}
      style={styleProps}
    >
      <NextImage
        style={{ objectFit, objectPosition }}
        className={clsx("max-w-full h-auto", imageClassName)}
        quality={quality ?? calcQuality(props)}
        {...props}
      />
      {children}
    </div>
  )
}
